import { Button, Card, CardContent, CardHeader, CardTitle, ScrollArea } from '@ibsys/kit';
import { XIcon } from 'lucide-react';
import { FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { RuleInfoSections } from './RuleInfoSections';
import { Position, useDraggablePosition } from './useDraggablePosition';

type Props = {
  initialPosition: Position;
  onClose: () => void;
};

/**
 * Плавающая справка по синтаксису правила извлечения.
 * Перетаскивается за шапку и закрывается только крестиком: пользователь правит правило
 * в поле ввода при открытой подсказке, поэтому клик вне, Esc и потеря фокуса её не закрывают.
 * Размер подсказки задаёт пользователь — нужный объём текста и свободное место на экране
 * заранее не известны.
 */
export const RuleInfoPopup: FC<Props> = ({ initialPosition, onClose }) => {
  const { t } = useTranslation('attributes');
  const { panelRef, panelStyle, dragHandlers } = useDraggablePosition(initialPosition);

  return createPortal(
    // resize с overflow даёт хэндл в правом нижнем углу; минимальные размеры не дают ужать панель до нечитаемой
    <div ref={panelRef} className="fixed z-50 h-112 w-120 min-h-40 min-w-64 resize overflow-hidden" style={panelStyle}>
      {/* Скругления и отступы — как у остальных окон системы */}
      <Card className="flex h-full flex-col gap-0 rounded-lg py-0 shadow-lg">
        <CardHeader className="flex shrink-0 items-center justify-between gap-2 border-b">
          {/* Ручка перетаскивания — только заголовок: захват указателя шапкой целиком забирал клик у крестика */}
          <div className="flex-1 cursor-move select-none" {...dragHandlers}>
            <CardTitle>{t('ruleInfo.title')}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={t('ruleInfo.close')}
            icon={<XIcon />}
            onClick={onClose}
          />
        </CardHeader>
        <CardContent className="min-h-0 flex-1 px-0 pb-0">
          <ScrollArea orientation="vertical" className={cn('h-full', SCROLL_AREA_VIEWPORT_BLOCK)}>
            <div className="px-6 pb-6">
              <RuleInfoSections />
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>,
    document.body,
  );
};
